import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Switch, FormControlLabel, Grid, Card, CardContent, CardActions,
    IconButton, Chip, Alert, CircularProgress, Tabs, Tab, List, ListItem,
    ListItemText, ListItemSecondaryAction, Divider, Accordion, AccordionSummary,
    AccordionDetails, Fab, Menu, MenuItem
} from '@mui/material';
import {
    Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as ViewIcon,
    VideoLibrary as VideoIcon, Description as DocumentIcon, CloudUpload as UploadIcon,
    DragHandle as DragIcon, ExpandMore as ExpandMoreIcon, Folder as CategoryIcon,
    Class as ClassIcon, MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { Editor } from '@tinymce/tinymce-react';
import axios from 'axios';

function TrainingManagement() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [classDialogOpen, setClassDialogOpen] = useState(false);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [editingClass, setEditingClass] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedClass, setSelectedClass] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploadType, setUploadType] = useState('category'); // 'category' or 'class'
    const [tabValue, setTabValue] = useState(0);
    const [expandedCategories, setExpandedCategories] = useState({});
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuTarget, setMenuTarget] = useState(null);

    // Category form state
    const [categoryFormData, setCategoryFormData] = useState({
        title: '',
        description: '',
        sort_order: 999,
        is_published: true
    });

    // Class form state
    const [classFormData, setClassFormData] = useState({
        title: '',
        description: '',
        vimeo_video_id: '',
        vimeo_video_url: '',
        vimeo_privacy_hash: '',
        content: '',
        sort_order: 999,
        is_published: true
    });

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/training/categories');
            setCategories(response.data.data || []);
        } catch (error) {
            console.error('Error fetching categories:', error);
        } finally {
            setLoading(false);
        }
    };

    // ===== CATEGORY HANDLERS =====

    const handleOpenCategoryDialog = (category = null) => {
        if (category) {
            setEditingCategory(category);
            setCategoryFormData({
                title: category.title,
                description: category.description || '',
                sort_order: category.sort_order || 999,
                is_published: category.is_published === 1
            });
        } else {
            setEditingCategory(null);
            setCategoryFormData({
                title: '',
                description: '',
                sort_order: 999,
                is_published: true
            });
        }
        setDialogOpen(true);
    };

    const handleSaveCategory = async () => {
        try {
            if (editingCategory) {
                await axios.put(`/api/training/categories/${editingCategory.id}`, categoryFormData);
            } else {
                await axios.post('/api/training/categories', categoryFormData);
            }
            
            fetchCategories();
            setDialogOpen(false);
            setEditingCategory(null);
        } catch (error) {
            console.error('Error saving category:', error);
            alert('Failed to save category');
        }
    };

    const handleDeleteCategory = async (categoryId) => {
        if (!window.confirm('Are you sure you want to delete this category? This will also delete all classes and documents within it.')) {
            return;
        }

        try {
            await axios.delete(`/api/training/categories/${categoryId}`);
            fetchCategories();
        } catch (error) {
            console.error('Error deleting category:', error);
            alert('Failed to delete category');
        }
    };

    // ===== CLASS HANDLERS =====

    const handleOpenClassDialog = (category, classData = null) => {
        setSelectedCategory(category);
        if (classData) {
            setEditingClass(classData);
            setClassFormData({
                title: classData.title,
                description: classData.description || '',
                vimeo_video_id: classData.vimeo_video_id || '',
                vimeo_video_url: classData.vimeo_video_url || '',
                vimeo_privacy_hash: classData.vimeo_privacy_hash || '',
                content: classData.content || '',
                sort_order: classData.sort_order || 999,
                is_published: classData.is_published === 1
            });
        } else {
            setEditingClass(null);
            setClassFormData({
                title: '',
                description: '',
                vimeo_video_id: '',
                vimeo_video_url: '',
                vimeo_privacy_hash: '',
                content: '',
                sort_order: 999,
                is_published: true
            });
        }
        setClassDialogOpen(true);
    };

    const handleSaveClass = async () => {
        try {
            if (editingClass) {
                await axios.put(`/api/training/classes/${editingClass.id}`, classFormData);
            } else {
                await axios.post(`/api/training/categories/${selectedCategory.id}/classes`, classFormData);
            }
            
            fetchCategories();
            setClassDialogOpen(false);
            setEditingClass(null);
            setSelectedCategory(null);
        } catch (error) {
            console.error('Error saving class:', error);
            alert('Failed to save class');
        }
    };

    const handleDeleteClass = async (classId) => {
        if (!window.confirm('Are you sure you want to delete this class? This will also delete all associated documents.')) {
            return;
        }

        try {
            await axios.delete(`/api/training/classes/${classId}`);
            fetchCategories();
        } catch (error) {
            console.error('Error deleting class:', error);
            alert('Failed to delete class');
        }
    };

    const handleViewClass = async (classData) => {
        try {
            const response = await axios.get(`/api/training/classes/${classData.id}`);
            setSelectedClass(response.data.data);
            setViewDialogOpen(true);
        } catch (error) {
            console.error('Error fetching class details:', error);
        }
    };

    // ===== UPLOAD HANDLERS =====

    const handleOpenUploadDialog = (type, target) => {
        setUploadType(type);
        if (type === 'category') {
            setSelectedCategory(target);
        } else {
            setSelectedClass(target);
        }
        setSelectedFiles([]);
        setUploadDialogOpen(true);
    };

    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        setSelectedFiles(files);
    };

    const handleUploadDocuments = async () => {
        if (!selectedFiles.length) {
            alert('Please select files to upload');
            return;
        }

        const formData = new FormData();
        selectedFiles.forEach(file => {
            formData.append('documents', file);
        });

        try {
            if (uploadType === 'category') {
                await axios.post(`/api/training/categories/${selectedCategory.id}/documents`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                await axios.post(`/api/training/classes/${selectedClass.id}/documents`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }
            
            alert('Documents uploaded successfully');
            setUploadDialogOpen(false);
            setSelectedFiles([]);
            fetchCategories();
        } catch (error) {
            console.error('Error uploading documents:', error);
            alert('Failed to upload documents');
        }
    };

    const handleDeleteDocument = async (type, targetId, documentId) => {
        if (!window.confirm('Are you sure you want to delete this document?')) {
            return;
        }

        try {
            if (type === 'category') {
                await axios.delete(`/api/training/categories/${targetId}/documents/${documentId}`);
            } else {
                await axios.delete(`/api/training/classes/${targetId}/documents/${documentId}`);
            }
            
            fetchCategories();
            
            // Refresh detailed view if open
            if (viewDialogOpen && selectedClass) {
                handleViewClass(selectedClass);
            }
        } catch (error) {
            console.error('Error deleting document:', error);
            alert('Failed to delete document');
        }
    };

    // ===== UTILITY FUNCTIONS =====

    const extractVimeoData = (input) => {
        if (!input) return { id: '', hash: '', url: '' };
        
        // Handle embed code (iframe src extraction)
        if (input.includes('<iframe')) {
            const srcMatch = input.match(/src="([^"]+)"/);
            if (srcMatch) {
                input = srcMatch[1];
            }
        }
        
        // Extract ID and hash from player URL
        const playerRegex = /player\.vimeo\.com\/video\/(\d+)(?:\?.*h=([a-zA-Z0-9]+))?/;
        const playerMatch = input.match(playerRegex);
        
        if (playerMatch) {
            return {
                id: playerMatch[1],
                hash: playerMatch[2] || '',
                url: input
            };
        }
        
        // Extract from regular vimeo.com URLs
        const vimeoRegex = /vimeo\.com\/(\d+)/;
        const vimeoMatch = input.match(vimeoRegex);
        
        if (vimeoMatch) {
            return {
                id: vimeoMatch[1],
                hash: '',
                url: input
            };
        }
        
        // If it's just a number, treat as ID
        if (/^\d+$/.test(input.trim())) {
            return {
                id: input.trim(),
                hash: '',
                url: `https://vimeo.com/${input.trim()}`
            };
        }
        
        return { id: '', hash: '', url: input };
    };

    const handleVimeoUrlChange = (input) => {
        const vimeoData = extractVimeoData(input);
        setClassFormData(prev => ({
            ...prev,
            vimeo_video_url: vimeoData.url,
            vimeo_video_id: vimeoData.id,
            vimeo_privacy_hash: vimeoData.hash
        }));
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const toggleCategoryExpansion = (categoryId) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    };

    // ===== MENU HANDLERS =====

    const handleMenuOpen = (event, type, target) => {
        setMenuAnchorEl(event.currentTarget);
        setMenuTarget({ type, target });
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuTarget(null);
    };

    const handleMenuAction = (action) => {
        const { type, target } = menuTarget;
        handleMenuClose();

        switch (action) {
            case 'edit':
                if (type === 'category') {
                    handleOpenCategoryDialog(target);
                } else {
                    handleOpenClassDialog(categories.find(c => c.classes.some(cl => cl.id === target.id)), target);
                }
                break;
            case 'upload':
                handleOpenUploadDialog(type, target);
                break;
            case 'delete':
                if (type === 'category') {
                    handleDeleteCategory(target.id);
                } else {
                    handleDeleteClass(target.id);
                }
                break;
            case 'view':
                if (type === 'class') {
                    handleViewClass(target);
                }
                break;
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">
                    Training Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenCategoryDialog()}
                >
                    Create Category
                </Button>
            </Box>

            {categories.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <CategoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        No training categories yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Create your first category to organize training classes
                    </Typography>
                    <Button variant="outlined" onClick={() => handleOpenCategoryDialog()}>
                        Create First Category
                    </Button>
                </Paper>
            ) : (
                <Box>
                    {categories.map((category) => (
                        <Accordion
                            key={category.id}
                            expanded={expandedCategories[category.id] || false}
                            onChange={() => toggleCategoryExpansion(category.id)}
                            sx={{ mb: 2 }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <CategoryIcon color="primary" />
                                        <Box>
                                            <Typography variant="h6">{category.title}</Typography>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Chip 
                                                    label={`Order: ${category.sort_order}`} 
                                                    size="small" 
                                                    color="primary"
                                                />
                                                <Chip 
                                                    label={`${category.class_count} classes`} 
                                                    size="small" 
                                                    color="info"
                                                />
                                                {category.documents && category.documents.length > 0 && (
                                                    <Chip 
                                                        icon={<DocumentIcon />}
                                                        label={`${category.documents.length} docs`} 
                                                        size="small" 
                                                        color="secondary"
                                                    />
                                                )}
                                                {category.is_published === 0 && (
                                                    <Chip 
                                                        label="Draft" 
                                                        size="small" 
                                                        color="warning"
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleMenuOpen(e, 'category', category);
                                        }}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box>
                                    {category.description && (
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            {category.description}
                                        </Typography>
                                    )}
                                    
                                    {/* Category Documents Section */}
                                    {category.documents && category.documents.length > 0 && (
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="h6" gutterBottom>Category Documents</Typography>
                                            <List dense>
                                                {category.documents.map((doc) => (
                                                    <React.Fragment key={doc.id}>
                                                        <ListItem>
                                                            <ListItemText
                                                                primary={doc.original_name}
                                                                secondary={`${formatFileSize(doc.file_size)} • ${doc.mime_type}`}
                                                            />
                                                            <ListItemSecondaryAction>
                                                                <IconButton 
                                                                    edge="end" 
                                                                    onClick={() => window.open(`/api/training/categories/${category.id}/documents/${doc.id}/download`)}
                                                                    sx={{ mr: 1 }}
                                                                >
                                                                    <DocumentIcon />
                                                                </IconButton>
                                                                <IconButton 
                                                                    edge="end" 
                                                                    color="error"
                                                                    onClick={() => handleDeleteDocument('category', category.id, doc.id)}
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </ListItemSecondaryAction>
                                                        </ListItem>
                                                        <Divider />
                                                    </React.Fragment>
                                                ))}
                                            </List>
                                        </Box>
                                    )}
                                    
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="h6">Classes</Typography>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<ClassIcon />}
                                            onClick={() => handleOpenClassDialog(category)}
                                        >
                                            Add Class
                                        </Button>
                                    </Box>
                                    
                                    {category.classes && category.classes.length > 0 ? (
                                        <Grid container spacing={2}>
                                            {category.classes.map((cls) => (
                                                <Grid item xs={12} md={6} lg={4} key={cls.id}>
                                                    <Card variant="outlined">
                                                        <CardContent>
                                                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                                                <Typography variant="subtitle1" noWrap>
                                                                    {cls.title}
                                                                </Typography>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => handleMenuOpen(e, 'class', cls)}
                                                                >
                                                                    <MoreVertIcon />
                                                                </IconButton>
                                                            </Box>
                                                            
                                                            <Typography variant="body2" color="text.secondary" mb={1}>
                                                                {cls.description || 'No description'}
                                                            </Typography>
                                                            
                                                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                                <Chip 
                                                                    label={cls.sort_order} 
                                                                    size="small" 
                                                                    color="primary"
                                                                />
                                                                {cls.vimeo_video_id && (
                                                                    <Chip 
                                                                        icon={<VideoIcon />} 
                                                                        label="Video" 
                                                                        size="small" 
                                                                        color="success"
                                                                    />
                                                                )}
                                                                {cls.document_count > 0 && (
                                                                    <Chip 
                                                                        icon={<DocumentIcon />} 
                                                                        label={`${cls.document_count} docs`} 
                                                                        size="small" 
                                                                        color="info"
                                                                    />
                                                                )}
                                                                {cls.is_published === 0 && (
                                                                    <Chip 
                                                                        label="Draft" 
                                                                        size="small" 
                                                                        color="warning"
                                                                    />
                                                                )}
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    ) : (
                                        <Box textAlign="center" py={3}>
                                            <ClassIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                                            <Typography variant="body2" color="text.secondary">
                                                No classes in this category yet
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            )}

            {/* Context Menu */}
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
            >
                {menuTarget?.type === 'category' && [
                    <MenuItem key="edit" onClick={() => handleMenuAction('edit')}>
                        <EditIcon sx={{ mr: 1 }} /> Edit Category
                    </MenuItem>,
                    <MenuItem key="upload" onClick={() => handleMenuAction('upload')}>
                        <UploadIcon sx={{ mr: 1 }} /> Upload Documents
                    </MenuItem>,
                    <MenuItem key="delete" onClick={() => handleMenuAction('delete')}>
                        <DeleteIcon sx={{ mr: 1 }} /> Delete Category
                    </MenuItem>
                ]}
                {menuTarget?.type === 'class' && [
                    <MenuItem key="view" onClick={() => handleMenuAction('view')}>
                        <ViewIcon sx={{ mr: 1 }} /> View Class
                    </MenuItem>,
                    <MenuItem key="edit" onClick={() => handleMenuAction('edit')}>
                        <EditIcon sx={{ mr: 1 }} /> Edit Class
                    </MenuItem>,
                    <MenuItem key="upload" onClick={() => handleMenuAction('upload')}>
                        <UploadIcon sx={{ mr: 1 }} /> Upload Documents
                    </MenuItem>,
                    <MenuItem key="delete" onClick={() => handleMenuAction('delete')}>
                        <DeleteIcon sx={{ mr: 1 }} /> Delete Class
                    </MenuItem>
                ]}
            </Menu>

            {/* Category Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingCategory ? 'Edit Category' : 'Create New Category'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <TextField
                            fullWidth
                            label="Category Title"
                            value={categoryFormData.title}
                            onChange={(e) => setCategoryFormData(prev => ({ ...prev, title: e.target.value }))}
                            required
                        />
                        
                        <TextField
                            fullWidth
                            label="Description"
                            value={categoryFormData.description}
                            onChange={(e) => setCategoryFormData(prev => ({ ...prev, description: e.target.value }))}
                            multiline
                            rows={3}
                        />
                        
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Sort Order"
                                    type="number"
                                    value={categoryFormData.sort_order}
                                    onChange={(e) => setCategoryFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 999 }))}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={categoryFormData.is_published}
                                            onChange={(e) => setCategoryFormData(prev => ({ ...prev, is_published: e.target.checked }))}
                                        />
                                    }
                                    label="Published"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveCategory} variant="contained">
                        {editingCategory ? 'Update Category' : 'Create Category'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Class Create/Edit Dialog */}
            <Dialog open={classDialogOpen} onClose={() => setClassDialogOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    {editingClass ? 'Edit Class' : `Create New Class in "${selectedCategory?.title}"`}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                            <Tab label="Basic Info" />
                            <Tab label="Video" />
                            <Tab label="Content" />
                        </Tabs>

                        {/* Basic Info Tab */}
                        {tabValue === 0 && (
                            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <TextField
                                    fullWidth
                                    label="Class Title"
                                    value={classFormData.title}
                                    onChange={(e) => setClassFormData(prev => ({ ...prev, title: e.target.value }))}
                                    required
                                />
                                
                                <TextField
                                    fullWidth
                                    label="Description"
                                    value={classFormData.description}
                                    onChange={(e) => setClassFormData(prev => ({ ...prev, description: e.target.value }))}
                                    multiline
                                    rows={3}
                                />
                                
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Sort Order"
                                            type="number"
                                            value={classFormData.sort_order}
                                            onChange={(e) => setClassFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 999 }))}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={classFormData.is_published}
                                                    onChange={(e) => setClassFormData(prev => ({ ...prev, is_published: e.target.checked }))}
                                                />
                                            }
                                            label="Published"
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {/* Video Tab */}
                        {tabValue === 1 && (
                            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <Alert severity="info">
                                    <strong>Paste any Vimeo URL or embed code:</strong><br />
                                    • Regular URL: https://vimeo.com/123456789<br />
                                    • Player URL: https://player.vimeo.com/video/123456789?h=abc123<br />
                                    • Full embed code: &lt;iframe src="..." ...&gt;<br />
                                    The system will automatically extract the video ID and privacy hash.
                                </Alert>
                                
                                <TextField
                                    fullWidth
                                    label="Vimeo URL or Embed Code"
                                    value={classFormData.vimeo_video_url}
                                    onChange={(e) => handleVimeoUrlChange(e.target.value)}
                                    placeholder="Paste Vimeo URL or full embed code here"
                                    multiline
                                    rows={3}
                                />
                                
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Video ID"
                                            value={classFormData.vimeo_video_id}
                                            onChange={(e) => setClassFormData(prev => ({ ...prev, vimeo_video_id: e.target.value }))}
                                            placeholder="123456789"
                                            helperText="Auto-extracted from URL"
                                            InputProps={{ readOnly: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Privacy Hash"
                                            value={classFormData.vimeo_privacy_hash}
                                            onChange={(e) => setClassFormData(prev => ({ ...prev, vimeo_privacy_hash: e.target.value }))}
                                            placeholder="abc123def456"
                                            helperText="Required for private/unlisted videos"
                                        />
                                    </Grid>
                                </Grid>
                                
                                {classFormData.vimeo_video_id && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>Video Preview:</Typography>
                                        <Box sx={{ position: 'relative', width: '100%', height: 0, paddingBottom: '56.25%' }}>
                                            <iframe
                                                src={`https://player.vimeo.com/video/${classFormData.vimeo_video_id}${classFormData.vimeo_privacy_hash ? `?h=${classFormData.vimeo_privacy_hash}&` : '?'}title=0&byline=0&portrait=0&badge=0&autopause=0`}
                                                frameBorder="0"
                                                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                                                referrerPolicy="strict-origin-when-cross-origin"
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%'
                                                }}
                                                allowFullScreen
                                                title="Video Preview"
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Content Tab */}
                        {tabValue === 2 && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Class Content (Rich Text)
                                </Typography>
                                <Editor
                                    apiKey={process.env.REACT_APP_TINYMCE_API_KEY || "cidq6j1ldl24ah599vsr9xb1rfl9ewlslc2cc1f3xoetd6zn"}
                                    value={classFormData.content}
                                    onEditorChange={(content) => setClassFormData(prev => ({ ...prev, content }))}
                                    init={{
                                        height: 400,
                                        menubar: false,
                                        plugins: [
                                            'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                            'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                            'insertdatetime', 'media', 'table', 'help', 'wordcount'
                                        ],
                                        toolbar: 'undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help',
                                        content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setClassDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveClass} variant="contained">
                        {editingClass ? 'Update Class' : 'Create Class'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Document Upload Dialog */}
            <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Upload Documents - {uploadType === 'category' ? selectedCategory?.title : selectedClass?.title}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Supported formats: PDF, Word (DOC/DOCX), PowerPoint (PPT/PPTX), images (JPG/PNG/GIF), and text files
                        </Alert>
                        
                        <input
                            type="file"
                            multiple
                            onChange={handleFileSelect}
                            accept=".pdf,.doc,.docx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
                            style={{ marginBottom: 16 }}
                        />
                        
                        {selectedFiles.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Selected Files:
                                </Typography>
                                <List dense>
                                    {selectedFiles.map((file, index) => (
                                        <ListItem key={index}>
                                            <ListItemText
                                                primary={file.name}
                                                secondary={formatFileSize(file.size)}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleUploadDocuments} variant="contained" disabled={!selectedFiles.length}>
                        Upload Documents
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Class View Dialog */}
            <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    Class: {selectedClass?.title}
                </DialogTitle>
                <DialogContent>
                    {selectedClass && (
                        <Box>
                            {selectedClass.vimeo_video_id && (
                                <Box sx={{ mb: 3, position: 'relative', width: '100%', height: 0, paddingBottom: '56.25%' }}>
                                    <iframe
                                        src={`https://player.vimeo.com/video/${selectedClass.vimeo_video_id}${selectedClass.vimeo_privacy_hash ? `?h=${selectedClass.vimeo_privacy_hash}&` : '?'}title=0&byline=0&portrait=0&badge=0&autopause=0`}
                                        frameBorder="0"
                                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                                        referrerPolicy="strict-origin-when-cross-origin"
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%'
                                        }}
                                        allowFullScreen
                                        title={selectedClass.title}
                                    />
                                </Box>
                            )}
                            
                            {selectedClass.content && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h6" gutterBottom>Content</Typography>
                                    <div dangerouslySetInnerHTML={{ __html: selectedClass.content }} />
                                </Box>
                            )}
                            
                            {selectedClass.documents && selectedClass.documents.length > 0 && (
                                <Box>
                                    <Typography variant="h6" gutterBottom>Documents</Typography>
                                    <List>
                                        {selectedClass.documents.map((doc) => (
                                            <React.Fragment key={doc.id}>
                                                <ListItem>
                                                    <ListItemText
                                                        primary={doc.original_name}
                                                        secondary={`${formatFileSize(doc.file_size)} • ${doc.mime_type}`}
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <IconButton 
                                                            edge="end" 
                                                            onClick={() => window.open(`/api/training/classes/${selectedClass.id}/documents/${doc.id}/download`)}
                                                        >
                                                            <DocumentIcon />
                                                        </IconButton>
                                                        <IconButton 
                                                            edge="end" 
                                                            color="error"
                                                            onClick={() => handleDeleteDocument('class', selectedClass.id, doc.id)}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                                <Divider />
                                            </React.Fragment>
                                        ))}
                                    </List>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default TrainingManagement;